import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as CategoryActions from './category.actions';
import * as AuthenticationActions from 'libs/authentication/src/lib/state/authentication.actions';

import { NotificationService } from '@rappider/services';
import { Navigate } from 'libs/shared/src/lib/states/router/router.actions';
import { PATH_DEFINITIONS } from '@rappider/shared/definitions';

/* Named import */
import { CategoryControllerService as ApiService } from '@rappider/rappider-sdk';
import * as ComponentDefinitionActions from 'libs/component-definition/src/lib/state/component-definition.actions';
import * as ProjectActions from 'libs/project/src/lib/states/projects-state/project.actions';


@Injectable()
export class CategoryEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private store: Store<any>,
    private notificationService: NotificationService
  ) { }

  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        <any>CategoryActions.GetCategories),
      withLatestFrom(
        this.store.select(state => state.activeProject.data?.id)
      ),
      mergeMap(([action, activeProjectId]) => {
        /* set a filter */
        const filter = {
          where: {
            or: [
              {
                projectId: activeProjectId
              },
              {
                projectId: undefined
              }
            ]
          },
          order: ['name ASC']
        };
        /* get the Categories */
        return this.apiService.find({ filter }).pipe(
          mergeMap(categories => [CategoryActions.GetCategoriesSuccessful({ payload: { categories } })]),
          catchError((error) => [
            CategoryActions.GetCategoriesFailure()
          ])
        );
      })
    )
  );

  createCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.CreateCategory),
      mergeMap((action) => {
        const params = {
          body: {
            ...action.payload.category
          }
        };

        return this.apiService.create(params).pipe(
          mergeMap((category) => {
            const actions: Action[] = [
              CategoryActions.CreateCategorySuccessful({ payload: { category: category } })
            ];

            this.notificationService.createNotification(
              'success',
              params.body.name,
              'SHARED.SUCCESSFULLY_CREATED'
            );
            if (category.type === 'project') {
              actions.push(
                new ProjectActions.AddCategoryToPublicProjectCategories({ projectCategory: category })
              );
            }
            if (action.payload.navigateToListAfterCreate) {
              actions.push(
                new Navigate({ url: PATH_DEFINITIONS.CATEGORIES.CATEGORY_LIST })
              );
            }
            return actions;
          }),
          catchError((error) => {
            const errorMessage = error?.error?.error?.message || 'SHARED.COULDNT_CREATED';
            this.notificationService.createNotification(
              'error',
              params.body.name,
              errorMessage
            );
            return [
              CategoryActions.CreateCategoryFailure(),
              CategoryActions.ErrorAction({ payload: { error: error, key: 'CreateCategory', timeStamp: Date.now() } })
            ];
          })
        );
      })
    )
  );

  updateCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.UpdateCategory),
      withLatestFrom(
        this.store.select(state => state.project.publicProjectCategories)
      ),
      mergeMap(([action, publicProjectCategories]) => {
        const params = {
          id: action.payload.categoryId,
          body: action.payload.category
        };


        const isExistInPublicProjectCategories = publicProjectCategories?.find?.(c => c.id === action.payload.categoryId) || null;

        return this.apiService.updateById(params).pipe(
          mergeMap((category) => {
            const actions: Action[] = [
              CategoryActions.UpdateCategorySuccessful({ payload: { categoryId: action.payload.categoryId, category: category } })
            ];

            this.notificationService.createNotification(
              'success',
              params.body.name,
              'SHARED.SUCCESSFULLY_UPDATED'
            );

            if (publicProjectCategories && isExistInPublicProjectCategories) {
              if (action.payload.category.type === 'project') {
                actions.push(
                  new ProjectActions.UpdateCategoryFromPublicProjectCategories({ projectCategory: category })
                );
              } else if (action.payload.category.type !== 'project') {
                actions.push(
                  new ProjectActions.RemoveCategoryFromPublicProjectCategories({ projectCategory: category })
                );
              }
            } else if (publicProjectCategories && action.payload.category.type === 'project') {
              actions.push(
                new ProjectActions.AddCategoryToPublicProjectCategories({ projectCategory: category })
              );
            }

            if (action.payload.navigateToListAfterUpdate) {
              actions.push(
                new Navigate({ url: PATH_DEFINITIONS.CATEGORIES.CATEGORY_LIST })
              );
            }
            return actions;
          }),
          catchError((error) => {

            const errorMessage = error?.error?.message || error?.message || 'SHARED.COULDNT_UPDATED';
            this.notificationService.createNotification(
              'error',
              params.body.name,
              errorMessage
            );
            return [
              CategoryActions.UpdateCategoryFailure(),
              CategoryActions.ErrorAction({ payload: { error: error, key: 'UpdateCategory', timeStamp: Date.now() } })
            ];
          })
        );
      })
    )
  );

  updateCategorySuccessful$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.UpdateCategorySuccessful),
      mergeMap(() => [
        CategoryActions.GetCategories()
      ])
    )
  );

  deleteCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.DeleteCategory),
      withLatestFrom(
        this.store.select(state => state.category?.data)
      ),
      mergeMap(([action, Categories]) => {
        const params = { id: action.payload.categoryId };
        const deletedCategory = Categories.find(category => category.id === action.payload.categoryId);

        return this.apiService.deleteById(params).pipe(
          map(() => {
            this.notificationService.createNotification(
              'success',
              deletedCategory.name,
              'SHARED.SUCCESSFULLY_DELETED'
            );
            return CategoryActions.GetCategories();
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              deletedCategory.name,
              'SHARED.COULDNT_DELETED'
            );
            return [
              CategoryActions.ErrorAction({ payload: { error: error, key: 'DeleteCategory', timeStamp: Date.now() } })
            ];
          })
        );
      })
    )
  );

  getPublicProjectsCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.ActionTypes.GetPublicProjectsCategories),
      mergeMap(() => {
        const params = {
          filter: {
            where: {
              type: 'project'
            },
            order: ['name ASC']
          }
        };
        return this.apiService.findPublicCategories(params).pipe(
          map(publicProjectsCategories =>
            new ProjectActions.GetPublicProjectsCategoriesSuccessful({ publicProjectsCategories })
          ),
          catchError((error) => [
            CategoryActions.GetCategoriesFailure()
          ])
        );
      })
    )
  );

}
